// ---------------------------------------------------------------------------
// NarrativeIsland — context-sensitive narrative panel for brainstorm overlay.
//
// Registered as `studio.brainstorm.narrative` in the host IslandRegistry.
//
// Two display modes:
//   tooltip — shown when activeId is set but expandedId is empty (hover/focus).
//             Displays just the headline in a floating badge.
//   panel   — shown when expandedId is set. Full headline + body + useCases.
//
// Fetches narrative from provider using the candidate's exampleId (not id).
// Cancels any in-flight request when activeId / expandedId change.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { getBrainstormProvider } from '../providers'
import type { Narrative } from '../repository'

interface NarrativeIslandProps {
  activeId?: string
  expandedId?: string
}

export function NarrativeIsland({ activeId = '', expandedId = '' }: NarrativeIslandProps) {
  const candidates = useStudioStore((s) => s.brainstorm.candidates)

  const mode: 'tooltip' | 'panel' | 'hidden' =
    expandedId ? 'panel' : activeId ? 'tooltip' : 'hidden'

  const targetId = expandedId || activeId
  const [narrative, setNarrative] = useState<Narrative | null>(null)

  useEffect(() => {
    if (!targetId) {
      setNarrative(null)
      return
    }
    const candidate = candidates.find((c) => c.id === targetId)
    if (!candidate) {
      setNarrative(null)
      return
    }
    let cancelled = false
    const provider = getBrainstormProvider()
    provider.getNarrative(candidate.exampleId).then((n) => {
      if (!cancelled) setNarrative(n)
    })
    return () => {
      cancelled = true
    }
  }, [targetId, candidates])

  if (mode === 'hidden' || !narrative) return null

  if (mode === 'tooltip') {
    return (
      <div
        data-testid="narrative-tooltip"
        className="pointer-events-none absolute bottom-4 right-[440px] max-w-xs rounded-md border border-border bg-background p-2 shadow-md text-xs"
      >
        {narrative.headline}
      </div>
    )
  }

  return (
    <aside
      data-testid="narrative-panel"
      className="pointer-events-auto absolute top-8 right-8 w-80 rounded-lg border border-border bg-background p-4 shadow-lg text-sm space-y-2"
    >
      <div className="font-semibold">{narrative.headline}</div>
      <p className="text-muted-foreground">{narrative.body}</p>
      {narrative.useCases && narrative.useCases.length > 0 && (
        <ul className="list-disc pl-4 text-xs text-muted-foreground">
          {narrative.useCases.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}
    </aside>
  )
}
